import { createTheme, ThemeOptions } from '@mui/material/styles';
import { koKR } from '@mui/material/locale';

/* ============================================================================
 * 일기월장 Design System — MUI Theme
 * Tokens는 src/styles/designTokens.css 에 단일 출처로 정의되어 있고,
 * 여기서는 MUI 컴포넌트가 인식할 수 있도록 동일 값들을 미러링한다.
 * ========================================================================== */

// ── Source of truth (CSS 토큰과 1:1 미러) ──────────────────────────────────
const tokens = {
  // brand / semantic
  primary: '#00BF40',
  primaryHover: '#009632',
  primaryPress: '#006E25',
  primarySoft: '#D9FFE6',
  primaryBg: '#F2FFF6',

  danger: '#FF4242',
  warning: '#FF9200',

  // surface
  bgBase: '#FFFFFF',
  bgElev1: '#FAFAFA',
  bgElev2: '#F7F7F8',
  bgMuted: '#F4F4F5',

  // foreground
  fgStrong: '#14191E',
  fgDefault: '#171719',
  fgSecondary: 'rgba(46, 47, 51, 0.88)',
  fgTertiary: 'rgba(55, 56, 60, 0.61)',
  fgDisabled: 'rgba(112, 115, 124, 0.28)',

  // border
  borderSubtle: 'rgba(112, 115, 124, 0.22)',
  borderDefault: 'rgba(112, 115, 124, 0.28)',
  borderInput: 'rgba(112, 115, 124, 0.8)',

  // financial
  income: '#00BF40',
  expense: '#FF4242',
  profit: '#006E25',
  loss: '#FF9200',
  neutral: '#878A93',
} as const;

const shadowFloating = '0 6px 16px rgba(23, 23, 23, 0.10), 0 2px 4px rgba(23, 23, 23, 0.06)';

// ── Color palette ───────────────────────────────────────────────────────────
const colors = {
  primary: {
    main: tokens.primary,
    light: '#49E57D',
    dark: tokens.primaryHover,
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: tokens.primaryHover,
    light: tokens.primary,
    dark: tokens.primaryPress,
    contrastText: '#FFFFFF',
  },
  success: {
    main: tokens.primary,
    light: '#49E57D',
    dark: tokens.primaryHover,
    contrastText: '#FFFFFF',
  },
  warning: {
    main: tokens.warning,
    light: '#FFB547',
    dark: '#D97A00',
    contrastText: '#FFFFFF',
  },
  error: {
    main: tokens.danger,
    light: '#FF7676',
    dark: '#D62F2F',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#46474C',
    light: '#878A93',
    dark: '#171719',
    contrastText: '#FFFFFF',
  },
  // 가계부 대시보드 전용 색상
  financial: {
    income: tokens.income,
    expense: tokens.expense,
    profit: tokens.profit,
    loss: tokens.loss,
    neutral: tokens.neutral,
  },
};

// ── Typography (디자인 시스템 6단계 + KPI 디스플레이) ──────────────────────
// 전 페이지 Pretendard 단일 폰트 통일 — designTokens.css 와 동기 (2026-06-13)
const fontSans =
  '"Pretendard Variable", "Pretendard", -apple-system, BlinkMacSystemFont, system-ui, "Apple SD Gothic Neo", "Noto Sans KR", sans-serif';
const fontDisplay = fontSans;
const fontMono = fontSans;

/* 40대 이상 타겟을 위한 가독성 사이징 — designTokens.css와 동기.
 * 원본 Wanted DS 스펙 대비 +2~3px 상향. */
const typography = {
  fontFamily: fontSans,
  // ─ Display (Pretendard + display weight) ─
  h1: {
    fontFamily: fontDisplay,
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.024em',
  },
  h2: {
    fontFamily: fontDisplay,
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.019em',
  },
  h3: {
    fontFamily: fontDisplay,
    fontSize: '19px',
    fontWeight: 700,
    lineHeight: 1.4,
    letterSpacing: '-0.012em',
  },
  h4: {
    fontSize: '19px',
    fontWeight: 700,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '17px',
    fontWeight: 700,
    lineHeight: 1.4,
  },
  h6: {
    fontSize: '15px',
    fontWeight: 700,
    lineHeight: 1.4,
  },
  subtitle1: {
    fontSize: '17px',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.014em',
  },
  subtitle2: {
    fontSize: '15px',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.014em',
  },
  body1: {
    fontSize: '17px',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.014em',
  },
  body2: {
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.014em',
  },
  button: {
    fontFamily: fontSans,
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0.014em',
    textTransform: 'none' as const,
  },
  caption: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0.025em',
  },
  overline: {
    fontSize: '13px',
    fontWeight: 700,
    lineHeight: 1.4,
    letterSpacing: '0.031em',
    textTransform: 'uppercase' as const,
  },
};

// ── Component overrides ────────────────────────────────────────────────────
const components: ThemeOptions['components'] = {
  MuiButtonBase: {
    defaultProps: { disableRipple: true },
    styleOverrides: {
      root: {
        '&.Mui-focusVisible': {
          outline: `2px solid ${tokens.primary}`,
          outlineOffset: 2,
        },
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: tokens.bgElev2,
        backgroundImage: 'none',
        color: tokens.fgDefault,
        fontFamily: fontSans,
        margin: 0,
        padding: 0,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      '*': {
        boxSizing: 'border-box',
      },
      // 숫자가 보이는 곳에선 tabular-nums (KPI/차트 라벨에서 자릿수 흔들림 방지)
      '.num, [data-num]': {
        fontFamily: fontMono,
        fontVariantNumeric: 'tabular-nums',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        // Quixotic 레퍼런스 — 버튼은 전부 필(pill)
        borderRadius: 9999,
        padding: '9px 18px',
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.014em',
        boxShadow: 'none',
        transition: 'all 160ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        '&:hover': {
          boxShadow: 'none',
        },
        '&:active': {
          transform: 'scale(0.98)',
        },
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      outlined: {
        borderColor: tokens.borderSubtle,
        '&:hover': {
          borderColor: tokens.borderDefault,
          backgroundColor: tokens.bgMuted,
        },
      },
      sizeSmall: {
        padding: '6px 12px',
        fontSize: 13,
      },
      sizeLarge: {
        padding: '12px 20px',
        fontSize: 15,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 20,
        backgroundColor: tokens.bgBase,
        border: `1px solid ${tokens.borderSubtle}`,
        boxShadow: 'none',
        transition: 'all 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 20,
        backgroundImage: 'none',
      },
      elevation0: {
        boxShadow: 'none',
        border: `1px solid ${tokens.borderSubtle}`,
      },
      elevation1: {
        boxShadow: 'none',
        border: `1px solid ${tokens.borderSubtle}`,
      },
      elevation2: {
        boxShadow: 'none',
        border: `1px solid ${tokens.borderSubtle}`,
      },
      elevation3: {
        boxShadow: 'none',
        border: `1px solid ${tokens.borderSubtle}`,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 12,
          backgroundColor: tokens.bgBase,
          '& fieldset': {
            borderColor: tokens.borderInput,
          },
          '&:hover fieldset': {
            borderColor: tokens.fgStrong,
          },
          '&.Mui-focused fieldset': {
            borderColor: tokens.primary,
            borderWidth: 1.5,
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 9999,
        fontWeight: 600,
        fontSize: 12,
        letterSpacing: '0.014em',
        height: 24,
      },
      sizeSmall: {
        height: 20,
        fontSize: 11,
      },
      filled: {
        backgroundColor: tokens.bgMuted,
        color: tokens.fgSecondary,
      },
      colorPrimary: {
        backgroundColor: tokens.primarySoft,
        color: tokens.primaryPress,
      },
      colorSuccess: {
        backgroundColor: tokens.primarySoft,
        color: tokens.primaryPress,
      },
      colorError: {
        backgroundColor: '#FFFAFA',
        color: tokens.danger,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: tokens.bgBase,
        color: tokens.fgStrong,
        borderBottom: `1px solid ${tokens.borderSubtle}`,
        boxShadow: 'none',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: tokens.bgBase,
        color: tokens.fgDefault,
        borderRight: `1px solid ${tokens.borderSubtle}`,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: tokens.borderSubtle,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: tokens.fgStrong,
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: 500,
        padding: '6px 10px',
        borderRadius: 8,
      },
      arrow: {
        color: tokens.fgStrong,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.014em',
      },
    },
  },
  // Quixotic 레퍼런스 — 세그먼트 토글: 연회색 필 트랙 + 선택 시 솔리드 그린 필
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        backgroundColor: tokens.bgMuted,
        borderRadius: 9999,
        padding: 3,
      },
      grouped: {
        border: 'none',
        borderRadius: '9999px !important',
        margin: 0,
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 13,
        border: 'none',
        borderRadius: 9999,
        padding: '5px 14px',
        color: tokens.fgSecondary,
        transition: 'all 160ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        '&.Mui-selected': {
          backgroundColor: tokens.primary,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: tokens.primaryHover,
          },
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: tokens.primary,
        height: 2,
        borderRadius: 1,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        backgroundColor: '#EAEBEC',
        height: 8,
      },
      bar: {
        borderRadius: 4,
        backgroundColor: tokens.primary,
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        color: tokens.borderDefault,
        '&.Mui-checked': {
          color: tokens.primary,
        },
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      switchBase: {
        '&.Mui-checked': {
          color: tokens.primary,
          '& + .MuiSwitch-track': {
            backgroundColor: tokens.primary,
          },
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottomColor: tokens.borderSubtle,
      },
      head: {
        fontWeight: 700,
        color: tokens.fgTertiary,
        fontSize: 11,
        letterSpacing: '0.031em',
        textTransform: 'uppercase',
        backgroundColor: tokens.bgElev2,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 20,
        border: `1px solid ${tokens.borderSubtle}`,
        boxShadow: shadowFloating,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
        border: `1px solid ${tokens.borderSubtle}`,
        boxShadow: `${shadowFloating} !important`,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 12, boxShadow: 'none', border: `1px solid ${tokens.borderSubtle}` },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: { '&:hover': { backgroundColor: tokens.bgMuted } },
    },
  },
};

// ── Theme factory ──────────────────────────────────────────────────────────
export const theme = createTheme(
  {
    palette: {
      ...colors,
      mode: 'light',
      background: {
        default: tokens.bgElev2,
        paper: tokens.bgBase,
      },
      text: {
        primary: tokens.fgDefault,
        secondary: tokens.fgSecondary,
        disabled: tokens.fgDisabled,
      },
      divider: tokens.borderSubtle,
    },
    typography,
    components,
    shape: {
      borderRadius: 12,
    },
    // 기존 컴포넌트들이 theme.spacing(2)=16px 등을 전제로 작성되어 있어 8 유지.
    // CSS 토큰의 4-multiple 시스템(--space-1=4px …)은 신규 작업에서 직접 사용.
    spacing: 8,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
  },
  koKR,
);

// 다크 테마는 디자인 시스템에서 'TBD'로 명시 — 현재 미정의.
// 추후 정의 시 bg/text 반전 + brand lightness 60~65%로 상향.
export const darkTheme = createTheme(
  {
    palette: {
      ...colors,
      mode: 'dark',
      background: {
        default: '#121212',
        paper: '#1E1E1E',
      },
      text: {
        primary: '#FFFFFF',
        secondary: '#B3B3B3',
        disabled: '#666666',
      },
    },
    typography,
    components,
    shape: {
      borderRadius: 12,
    },
    spacing: 8,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
  },
  koKR,
);

// 테마 타입 내보내기
export type AppTheme = typeof theme;
export type AppThemeOptions = ThemeOptions;

// 색상 유틸리티 함수들
export const getFinancialColor = (type: 'income' | 'expense' | 'profit' | 'loss' | 'neutral') => {
  return colors.financial[type];
};

export const getContrastText = (backgroundColor: string) => {
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#FFFFFF';
};
